document.addEventListener("DOMContentLoaded", () => {
  const cartFunctions = themeUtils.cartFunctions;
  const formatPrice = themeUtils.general.formatPrice;
  const genderDict = {
    WOMEN: "women",
    MEN: "men",
    NONE: "none",
    BOTH: "both",
  };
  const genderTagDict = {
    WOMEN_TAG: "Gender: Women",
    MEN_TAG: "Gender: Men",
    ACCESSORIES_TAG: "Style: Accessories",
  };

  const { createApp, computed, ref, reactive, onBeforeUnmount, onBeforeMount } =
    Vue;

  const Cartitemcomponent = {
    name: "CartItem",
    delimiters: ["{(", ")}"],
    props: {
      item: Object,
      currency: String,
    },
    setup(props) {
      const isCadOrUsd = computed(() => {
        return ["CAD", "USD"].includes(props.currency);
      });
      const isDiscounted = computed(() => props.item.total_discount > 0);
      const productTitle = computed(() => {
        // remove everything after the first " - "
        return (
          props.item?.product_title.split(" - ")[0].trim() || props.item?.title
        );
      });
      const showSaleBadges = cartSettings?.cartShowSaleBadges || false;
      const totalDiscountWithoutUpsell = computed(() => {
        return props.item?.discounts.reduce((acc, discount) => {
          if (discount.title.toLowerCase().includes("upsell")) return acc;
          return acc + discount.amount;
        }, 0);
      });

      function roundMoneyValue(value) {
        // Separate the integer and "decimal" part (last two digits)
        const integerPart = Math.floor(value / 100); // Get the whole part
        const decimalPart = value % 100; // Get the last two digits

        // Determine if we round up or not
        if (decimalPart >= 50) {
          return (integerPart + 1) * 100; // Round up
        } else {
          return integerPart * 100; // Keep the lower value
        }
      }

      // if the item has a handle that contains `certificate` remove itself from the cart
      if (props.item?.handle?.includes("certificate")) {
        cartFunctions.updateCartItemQuantity(props.item, 0);
      }
      if (props.item?.handle?.includes("stratus-tote")) {
        cartFunctions.updateCartItemQuantity(props.item, 0);
      }

      return {
        formatPrice,
        isCadOrUsd,
        isDiscounted,
        productTitle,
        roundMoneyValue,
        showSaleBadges,
        updateCartItemQuantity: cartFunctions.updateCartItemQuantity,
        totalDiscountWithoutUpsell,
      };
    },
    template: `
    <li class="flex wrap">
    <img
      class="rounded" 
      :src="item.featured_image.url"
      :alt="item.featured_image.alt"
      :key="item.featured_image.id"
      :width="item.featured_image.width"
      :height="item.featured_image.height"
      loading="lazy"
    >
    <div class="cart_item__details">
      <div class="cart_item__head flex">
        <a :href="item.url" class="cart_item__title subheading bold flex column">
          {( productTitle )}
          <span class="text flex column gap_sm">
            {( item.variant_title )}
            <small class="cart_item_notes" v-if="item.properties.Notes">{( item.properties.Notes )}</small>
            <small class="cart_item_notes" v-if="item.properties['Giving Week']">{( item.properties['Giving Week'] )}</small>
          </span>
        </a>
        <div class="cart_item_prices_wrapper">
          <div class="cart_item_price">
            <span 
              v-if="totalDiscountWithoutUpsell" 
              class="slashed_original_price text"
            >
              {( formatPrice(item.original_line_price, currency, !totalDiscountWithoutUpsell || isCadOrUsd) )}
            </span>
            <span 
              class="text"
              :class="{ sale_price: totalDiscountWithoutUpsell }">
              {( formatPrice(isDiscounted ? roundMoneyValue(item.original_line_price - totalDiscountWithoutUpsell) : item.line_price, currency, isCadOrUsd) )}
            </span>
          </div>
        </div>
      </div>
      <div class="cart_item__update">
        <div class="cart_item_qty_update_wrapper flex justify_center align_center">
          <button @click="updateCartItemQuantity(item, item.quantity - 1)" :disabled="item.quantity <= 1">
           <svg class="v-icon bg_primary circle">
              <use xlink:href="#v-icon-minus"></use>
            </svg>
          </button>
          <span class="subheading bold">{( item.quantity )}</span>
          <button @click="updateCartItemQuantity(item, item.quantity + 1)">
            <svg class="v-icon bg_primary circle">
              <use xlink:href="#v-icon-plus"></use>
            </svg>
          </button>
        </div>
        <span class="cart_item__remove flex align_center justify_center" @click="updateCartItemQuantity(item, 0)">
          <svg class="v-icon v-icon-stroke">
              <use xlink:href="#v-icon-stroke-bin"></use>
          </svg>
        </span>
      </div>
    </div>
    <div v-if="item.discounts.length && showSaleBadges" class="cart_item_tags flex_item_full flex justify_end">
      <small v-for="discount in item.discounts" :key="discount.title" class="cart_item_tag">{( discount.title )}</small>
    </div>
    </li>
    `,
  };

  const cartComponent = {
    name: "Cart",
    delimiters: ["{(", ")}"],
    components: {
      Cartitemcomponent,
      Completethelook,
    },
    setup() {
      //DATA
      const cart = reactive({});
      const isLoading = ref(false);
      const currency = cart?.currency || CURRENCY;
      const isCadOrUsd = computed(() => {
        return ["CAD", "USD"].includes(currency);
      });
      const showEDD = cartSettings.cartShowEdd;
      const showOriginalTotalPrice = cartSettings.cartShowOriginalTotalPrice;
      const showUpsellPromo = cartSettings.cartShowUpsellPromo;
      const showSaleBadges = cartSettings.cartShowSaleBadges;
      const freeShippingThreshold = cartSettings.cartFreeShippingThreshold;
      const otherItemsThresholds = cartSettings.cartOtherItemsThresholds;
      const otherItemsThresholdsNames =
        cartSettings.cartOtherItemsThresholdsNames;
      const upsellTag = cartSettings.cartUpsellTag;
      const isVip = cartSettings.cartIsVip;
      const hasVipCookie = themeUtils.general.hasCookie("vessi_vip");
      const activeSlide = ref(0);
      const slideDirection = ref("slide-next");
      const touchStart = ref(0);
      const touchEnd = ref(0);
      const shouldShowVip = true;
      const giftThreshold = 125;

      //Computed
      const giftCount = computed(() => {
        const giftCount = Math.floor(
          totalCartPriceWithoutGiftcard.value / (giftThreshold * 100)
        );
        return giftCount;
      });
      const valueForNextGift = computed(() => {
        const rawValue =
          (giftCount.value + 1) * giftThreshold * 100 -
          totalCartPriceWithoutGiftcard.value;
        const stepItemTarget = document.querySelector(
          ".cart-stepper-item.completed:nth-child(3)"
        );
        if (stepItemTarget) {
          // set the width of ::after element to the percentage of the value only for this step
          const stepWidth = 1 - rawValue / (giftThreshold * 100); // No extra *100
          const weightedValue = Math.max(0, Math.min(1, stepWidth)); // Normalize to [0, 1]
          const weightedValuePercentage = 70 + (100 - 70) * weightedValue;
          stepItemTarget.style.setProperty(
            "--to-complete-width",
            `${weightedValuePercentage}%`
          );
        }
        return formatPrice(rawValue, currency, isCadOrUsd.value);
      });

      const gwpOffers = computed(() => {
        const gwpCounts = [];
        const cartItems = cart?.items || [];
        const cartGwpItems = {};

        // Count items already in the cart
        for (const item of cartItems) {
          const handle = item.handle;
          if (handle) {
            cartGwpItems[handle] = item.quantity;
          }
        }

        for (const item of cartItems) {
          const gwpProducts = item.properties?._GWP_PRODUCTS || [];
          const quantity = item.quantity || 0;

          if (gwpProducts.length) {
            for (let i = 0; i < quantity; i++) {
              gwpCounts.push(gwpProducts);
            }
          }
        }

        for (const handle in cartGwpItems) {
          const handleQty = cartGwpItems[handle];
          if (handleQty <= 0) continue;
          for (const gwpCount of gwpCounts) {
            if (gwpCount.includes(handle)) {
              for (let i = 0; i < handleQty; i++) {
                // get the index of gwpCount that contains the handle
                const index = gwpCounts.indexOf(gwpCount);
                // remove gwpCount from gwpCounts
                gwpCounts.splice(index, 1);
              }
              cartGwpItems[handle]--;
              break;
            }
          }
        }

        const spreadGwpCounts = new Set(gwpCounts.flat());

        return Array.from(spreadGwpCounts);
      });

      const upsellDiscount = computed(() => {
        if (!cart?.cart_level_discount_applications) return 0;
        // cart.cart_level_discount_applications
        const upsellDiscount = cart?.cart_level_discount_applications?.find(
          (discount) => {
            if (discount.discount_application) {
              return discount.discount_application.title
                .toLowerCase()
                .includes("upsell");
            } else {
              return discount.title.toLowerCase().includes("upsell");
            }
          }
        );
        const upsellDiscountAmount = !upsellDiscount
          ? 0
          : upsellDiscount?.discount_application
            ? upsellDiscount.amount ||
              upsellDiscount.discount_application.total_allocated_amount
            : upsellDiscount.total_allocated_amount;
        return upsellDiscountAmount;
      });

      const categorizeCartByGender = computed(() => {
        const hasWomen =
          cart?.items?.some((item) =>
            item?.handle?.startsWith(genderDict.WOMEN)
          ) || false;
        const hasMen =
          cart?.items?.some((item) =>
            item?.handle?.startsWith(genderDict.MEN)
          ) || false;

        if (hasWomen && hasMen) return genderDict.BOTH;
        if (hasWomen) return genderDict.WOMEN;
        if (hasMen) return genderDict.MEN;
        return genderDict.NONE;
      });
      const amountToFreeShipping = computed(() => {
        return totalCartPriceWithoutGiftcard.value >=
          freeShippingThreshold * 100
          ? 0
          : freeShippingThreshold - totalCartPriceWithoutGiftcard.value;
      });
      const isCartEmpty = computed(
        () => cart?.item_count === 0
      );
      const isFreeShipping = computed(() => {
        return (
          totalCartPriceWithoutGiftcard.value >= freeShippingThreshold * 100
        );
      });
      const totalCartPriceWithoutGiftcard = computed(() => {
        if (!cart?.items) return 0;
        const totalCartPrice = cart?.total_price;
        const giftCardPrice =
          cart?.items?.filter((item) => item?.handle === "giftcard")[0]
            ?.original_line_price || 0;

        return totalCartPrice - giftCardPrice;
      });
      const allThresholds = computed(() => {
        let thresholdArray = [
          {
            type: "free_shipping",
            name: "Free Shipping",
            value: parseFloat(freeShippingThreshold) * 100,
            isCompleted:
              totalCartPriceWithoutGiftcard?.value >=
              parseFloat(freeShippingThreshold) * 100,
          },
        ];

        if (shouldShowVip) {
          thresholdArray = [
            ...otherItemsThresholds.map((threshold, index) => ({
              type: "other",
              name: otherItemsThresholdsNames[index]?.trim() || "Special Offer",
              value: parseFloat(threshold) * 100,
              isCompleted:
                totalCartPriceWithoutGiftcard?.value >=
                parseFloat(threshold) * 100,
            })),
            {
              type: "free_shipping",
              name: "Free Shipping",
              value: parseFloat(freeShippingThreshold) * 100,
              isCompleted:
                totalCartPriceWithoutGiftcard?.value >=
                parseFloat(freeShippingThreshold) * 100,
            },
          ].sort((a, b) => a.value - b.value);
        }

        // get the next element that is not completed
        const nextThreshold =
          thresholdArray.find((threshold) => !threshold.isCompleted) ||
          thresholdArray[thresholdArray.length - 1];

        const lastCompletedThreshold = [...thresholdArray]
          .reverse()
          .find((threshold) => threshold.isCompleted) || { value: 0 };

        if (nextThreshold) {
          const value =
            ((totalCartPriceWithoutGiftcard.value -
              lastCompletedThreshold.value) /
              (nextThreshold.value - lastCompletedThreshold.value)) *
            100;
          updateStepperCompletedWidth(
            isNaN(value) || value === Infinity ? 100 : value
          );
        }

        return thresholdArray;
      });
      const hasOnlyGiftcard = computed(() => {
        return !cart?.items?.some((item) => item?.handle !== "giftcard");
      });
      const hasOtherItemsInCart = computed(() => {
        return cart?.items?.some(
          (item) => item?.product_type.toLowerCase() !== "shoes"
        );
      });
      const hasShoeInCart = computed(() => {
        return cart?.items?.some(
          (item) => item?.product_type.toLowerCase() === "shoes"
        );
      });
      const hasAstoriaInCart = computed(() => {
        return cart?.items?.some((item) => item?.handle === "astoria");
      });

      const hasOvercastJacketInCart = computed(() => {
        return cart?.items?.some((item) => item?.handle === "overcast-jacket");
      });

      const bfcmSaleItemCount = ref(0);

      const countItemsInCart = computed(() => {
        let socksCount = 0;
        let shoesCount = 0;
        cart?.items?.forEach((item) => {
          if (item?.product_type.toLowerCase() === "socks") {
            socksCount += item.quantity;
          }
          if (item.product_type.toLowerCase() === "shoes") {
            themeUtils.productFunctions
              .fetchProductFromWorker(item.handle, window.vessiWebWorker)
              .then((data) => {
                if (data.product.tags.includes("BFCM Sale Page")) {
                  shoesCount += item.quantity;
                  bfcmSaleItemCount.value = shoesCount;
                }
              });
          }
        });

        return {
          socksCount,
          bfcmSaleItemCount,
        };
      });

      const displayBfcmCrossSell = computed(() => {
        return bfcmSaleItemCount.value > countItemsInCart.value.socksCount;
      });

      const eddForCart = ref(JSON.parse(localStorage.getItem("edd")));
      window.addEventListener("edd_updated", () => {
        eddForCart.value = JSON.parse(localStorage.getItem("edd"));
      });

      const upsellList = computed(() => {
        let list;

        if (gwpOffers.value.length) {
          list = gwpOffers.value.map((handle) => {
            return {
              handle,
            };
          });
          return list;
        }

        if (
          shouldShowUpsellPromo.value &&
          cartSettings?.cartCrossSellUpsell &&
          shouldShowVip
        ) {
          list = cartSettings.cartCrossSellUpsell;
          list = list?.filter((item) => {
            if (categorizeCartByGender.value === genderDict.BOTH)
              return item.available;
            if (categorizeCartByGender.value === genderDict.NONE)
              return item.available;
            if (categorizeCartByGender.value === genderDict.WOMEN)
              return item.tags.some(
                (tag) =>
                  tag === genderTagDict.WOMEN_TAG ||
                  tag === genderTagDict.ACCESSORIES_TAG
              );
            if (categorizeCartByGender.value === genderDict.MEN)
              return item.tags.some(
                (tag) =>
                  tag === genderTagDict.MEN_TAG ||
                  tag === genderTagDict.ACCESSORIES_TAG
              );
          });
          return list;
        }

        if (hasShoeInCart.value && hasOtherItemsInCart.value) {
          list = cartSettings.cartCrossSellAll;
        } else if (hasShoeInCart.value) {
          list = cartSettings.cartCrossSellShoes;
        } else {
          list = cartSettings.cartCrossSellOthers;
        }

        if (!list || !Array.isArray(list)) return [];

        list = list?.filter((item) => {
          if (categorizeCartByGender.value === genderDict.BOTH) return true;
          if (categorizeCartByGender.value === genderDict.NONE) return true;
          if (categorizeCartByGender.value === genderDict.WOMEN)
            return item.tags.some((tag) => {
              return (
                tag === genderTagDict.WOMEN_TAG ||
                tag === genderTagDict.ACCESSORIES_TAG
              );
            });
          if (categorizeCartByGender.value === genderDict.MEN)
            return item.tags.some(
              (tag) =>
                tag === genderTagDict.MEN_TAG ||
                tag === genderTagDict.ACCESSORIES_TAG
            );
        });
        // filter out if product with same handle is already in cart
        list = list?.filter((item) => {
          return (
            !cart?.items?.some((cartItem) => cartItem.handle === item.handle) &&
            item.available
          );
        });

        return list.slice(0, 15);
      });

      const shouldShowUpsellPromo = computed(() => {
        // check if any of the cart items has the upsell tag
        const cartHasUpsellTag = cart?.items?.some((item) => {
          // check if discounts array has a title that contains "sale"
          return item?.discounts?.some((discount) =>
            discount.title.toLowerCase().includes("upsell")
          );
        });
        return (
          showUpsellPromo &&
          !cartHasUpsellTag &&
          (hasShoeInCart.value ||
            hasAstoriaInCart.value ||
            hasOvercastJacketInCart.value)
        );
      });

      //Methods
      const handleTouchStart = (e) => {
        touchStart.value = e.changedTouches[0].clientX;
      };

      const handleTouchEnd = (e) => {
        touchEnd.value = e.changedTouches[0].clientX;
        const threshold = 100; // Set your desired threshold value here
        if (Math.abs(touchEnd.value - touchStart.value) < threshold) return;
        if (touchEnd.value < touchStart.value) {
          nextSlide(); // Swiped left
        } else if (touchEnd.value > touchStart.value) {
          prevSlide(); // Swiped right
        }
      };

      let isDragging = ref(false);

      const handleMouseDown = (e) => {
        isDragging.value = true;
        touchStart.value = e.clientX;
      };

      const handleMouseMove = (e) => {
        if (!isDragging.value) return;
        touchEnd.value = e.clientX;
        const threshold = 100; // Set your desired threshold value here
        if (Math.abs(touchEnd.value - touchStart.value) < threshold) return;
        if (touchEnd.value < touchStart.value) {
          nextSlide(); // Dragged left
        } else if (touchEnd.value > touchStart.value) {
          prevSlide(); // Dragged right
        }
        touchStart.value = touchEnd.value;
        handleMouseUp();
      };

      const handleMouseUp = () => {
        isDragging.value = false;
      };

      onBeforeUnmount(() => {
        window.removeEventListener("mousedown", handleMouseDown);
        window.removeEventListener("mouseup", handleMouseUp);
      });

      const nextSlide = () => {
        if (activeSlide.value < upsellList.value.length - 1) {
          activeSlide.value++;
        } else {
          activeSlide.value = 0;
        }
        slideDirection.value = "slide-right";
      };
      const prevSlide = () => {
        if (activeSlide.value > 0) {
          activeSlide.value--;
        } else {
          activeSlide.value = upsellList.value.length - 1;
        }
        slideDirection.value = "slide-left";
      };
      const changeSlide = (index) => {
        activeSlide.value = index;
      };
      const updateCart = (newCartData) => {
        const itemCount = newCartData?.item_count;
        const cartItemCount = cart?.item_count;
        if (itemCount !== cartItemCount) {
          changeSlide(0);
        }

        if (!newCartData?.token) {
          cartFunctions.fetchCart();
        } else {
          for (const key in newCartData) {
            if (Object.hasOwn(newCartData, key)) {
              cart[key] = newCartData[key];
            }
          }
        }
      };
      const updateStepperCompletedWidth = (value) => {
        const cartStepperWrapper = document.querySelector(
          ".cart-stepper-wrapper"
        );
        // get last value of completed width
        const lastCompletedWidth = parseFloat(
          cartStepperWrapper?.style.getPropertyValue("--completed-width")
        );
        if (value === lastCompletedWidth) return;
        // the new value is less than the last value, remove the transition
        if (value < lastCompletedWidth) {
          cartStepperWrapper?.classList.add("no-transition");
          cartStepperWrapper?.style.setProperty(
            "--completed-width",
            `${value}%`
          );
          // reflow
          void cartStepperWrapper?.offsetHeight;
          // add the transition back
          setTimeout(() => {
            cartStepperWrapper?.classList.remove("no-transition");
          }, 600);
        } else {
          cartStepperWrapper?.style.setProperty(
            "--completed-width",
            `${value}%`
          );
        }
      };
      const onCartRequest = (event) => {
        if (event.detail.status === 200) {
          updateCart(event.detail.cart);
        } else if (event.detail.status === 422) {
          let message = event.detail.cart?.message;
          if (event.detail.cart?.description) {
            message += `: ${event.detail.cart?.description}`;
          }
          themeUtils.general.toastMessage(message, 3000, "warning");
        } else {
          console.error("Error:", event.detail.status);
        }
      };
      document.addEventListener("cart_request", onCartRequest);

      // Lifecycle
      //before mount
      onBeforeMount(() => {
        cartFunctions.fetchCart();
      });

      onBeforeUnmount(() => {
        document.removeEventListener("cart_request", onCartRequest);
      });

      return {
        activeSlide,
        allThresholds,
        amountToFreeShipping,
        bfcmSaleItemCount,
        cart,
        categorizeCartByGender,
        closeCart: cartFunctions.closeCart,
        countItemsInCart,
        currency,
        displayBfcmCrossSell,
        eddForCart,
        formatPrice,
        giftCount,
        isCadOrUsd,
        isCartEmpty,
        isLoading,
        isFreeShipping,
        changeSlide,
        gwpOffers,
        handleTouchStart,
        handleTouchEnd,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        hasOnlyGiftcard,
        hasOtherItemsInCart,
        hasShoeInCart,
        openCart: cartFunctions.openCart,
        nextSlide,
        prevSlide,
        slideDirection,
        shouldShowUpsellPromo,
        shouldShowVip,
        showEDD,
        showOriginalTotalPrice,
        showSaleBadges,
        totalCartPriceWithoutGiftcard,
        upsellDiscount,
        upsellList,
        upsellTag,
        valueForNextGift,
      };
    },
  };

  const cartApp = createApp(cartComponent);
  const mountCartListener = window.addEventListener("mount_cart_app", () => {
    cartApp.mount("#cart-template");
    window.isCartMounted = true;
    window.removeEventListener("mount_cart_app", mountCartListener);
  });
});
